import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import {
  PermApiResponse,
  RoleApiResponse,
  RoleContextType,
} from "../interface/interfaces";
import { Permission, TeamsTableApiResponse } from "../interface/types";
import useApiHook from "../utils/useApi";

const AdminRoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const PAGE_SIZE = 10;
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [activeName, setActiveName] = useState<string | null>(null);
  const [activeRolePermissions, setActiveRolePermissions] = useState<string[]>(
    []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [allAvailablePermissions, setAllAvailablePermissions] = useState<
    Permission[]
  >([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [shouldReset, setShouldReset] = useState<boolean>(false);

  const { data, isLoading, makeRequest } = useApiHook<RoleApiResponse>(
    "/admin/roles",
    {
      method: "get",
    }
  );

  const {
    data: sysData,
    isLoading: sysLoading,
    makeRequest: systemReq,
  } = useApiHook<PermApiResponse>("/admin/system-role", {
    method: "get",
  });

  const {
    data: subData,
    isLoading: subLoading,
    makeRequest: subReq,
  } = useApiHook<TeamsTableApiResponse>(
    activeRole ? `/admin/role-access/${activeRole}` : "",
    {
      method: "get",
    }
  );

  // Reset function to reinitialize the context
  const resetContext = useCallback(() => {
    setActiveRole(null);
    setActiveName(null);
    setActiveRolePermissions([]);
    setCurrentPage(1);
    setAllAvailablePermissions([]);
    setIsInitialized(false);
    setShouldReset(true);
  }, []);

  // Initial API calls - ensure they fire immediately
  useEffect(() => {
    makeRequest({ page: currentPage, size: PAGE_SIZE });
    systemReq();
  }, [currentPage, shouldReset]);

  // Reset shouldReset flag after API calls
  useEffect(() => {
    if (shouldReset) {
      setShouldReset(false);
    }
  }, [shouldReset]);

  // Update available permissions when system data loads
  useEffect(() => {
    if (sysData?.data?.rolePermissions) {
      setAllAvailablePermissions(sysData.data.rolePermissions);
    }
  }, [sysData]);

  // Fire subReq whenever activeRole changes (and it's not null)
  useEffect(() => {
    if (activeRole) {
      subReq();
    }
  }, [activeRole]);

  // Initialize default role - Fixed logic with proper dependency handling
  useEffect(() => {
    // Only initialize once and when we have the required data
    if (
      !isInitialized &&
      data?.data?.data &&
      data.data.data.length > 0 &&
      sysData?.data?.rolePermissions &&
      sysData.data.rolePermissions.length > 0
    ) {
      const firstDefaultRole = data.data.data.find(
        (role) => !role.isCustomRole
      );

      if (firstDefaultRole) {
        setActiveRole(firstDefaultRole.roleReference);
        setActiveRolePermissions(firstDefaultRole.rolePermissions);
        setActiveName(firstDefaultRole.roleName);
      } else {
        const firstRole = data.data.data[0];
        setActiveRole(firstRole.roleReference);
        setActiveRolePermissions(firstRole.rolePermissions);
        setActiveName(firstRole.roleName);
      }

      setIsInitialized(true);
    }
  }, [data, sysData, isInitialized]);

  const defaultRoles =
    data?.data?.data?.filter((role) => !role.isCustomRole) || [];
  const customRoles =
    data?.data?.data?.filter((role) => role.isCustomRole) || [];

  const handleRoleClick = (
    roleReference: string,
    rolePermissions: string[],
    roleName: string
  ) => {
    setActiveRole(roleReference);
    setActiveRolePermissions(rolePermissions);
    setActiveName(roleName);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollTop + clientHeight >= scrollHeight - 50) {
      if (data?.data?.totalPages && currentPage < data.data.totalPages) {
        setCurrentPage(currentPage + 1);
      }
    }
  };

  // Format team member names (combining first name and last name with comma separation)
  const formatTeamMemberNames = () => {
    if (!subData?.data || subData.data.length === 0) {
      return "";
    }

    // Combine first and last names for each team member
    const fullNames = subData.data.map((member) =>
      `${member.userFirstName || ""} ${member.userLastName || ""}`.trim()
    );

    // For many names, truncate and add ellipsis to maintain good UX
    if (fullNames.length > 4) {
      return `${fullNames.slice(0, 4).join(", ")}, and ${
        fullNames.length - 4
      } more`;
    }

    // For 2 or more names, connect the last name with "and"
    if (fullNames.length > 1) {
      return `${fullNames.slice(0, -1).join(", ")} and ${
        fullNames[fullNames.length - 1]
      }`;
    }

    // If just one name
    return fullNames[0];
  };

  const teamMembersCount = subData?.data?.length || 0;
  const teamMembersText =
    teamMembersCount === 1 ? "Team Member" : "Team Members";
  const teamMemberNames = formatTeamMemberNames();

  const missingPermissions = allAvailablePermissions.filter(
    (permission) => !activeRolePermissions.includes(permission)
  );

  const hasFullAccess =
    missingPermissions.length === 0 && allAvailablePermissions.length > 0;

  const isActiveRoleCustom = data?.data?.data?.find(
    (role) => role.roleReference === activeRole
  )?.isCustomRole;

  // Improved loading state logic
  const isInitialLoading = Boolean(
    (isLoading && currentPage === 1) || sysLoading || !isInitialized
  );

  return (
    <AdminRoleContext.Provider
      value={{
        isLoading,
        data,
        sysData,
        sysLoading,
        subData,
        subLoading,
        activeRole,
        activeName,
        activeRolePermissions,
        currentPage,
        allAvailablePermissions,
        defaultRoles,
        customRoles,
        teamMembersCount,
        teamMembersText,
        teamMemberNames,
        missingPermissions,
        hasFullAccess,
        isActiveRoleCustom: Boolean(isActiveRoleCustom),
        setActiveRole,
        setActiveName,
        setActiveRolePermissions,
        setCurrentPage,
        handleRoleClick,
        handleScroll,
        resetContext, 
      }}
    >
      {children}
    </AdminRoleContext.Provider>
  );
};

export const useRoleContext = () => {
  const context = useContext(AdminRoleContext);
  if (context === undefined) {
    throw new Error("useRoleContext must be used within a RoleProvider");
  }
  return context;
};
